import React from 'react';
import { Modal } from '@mantine/core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { IconProp } from '@fortawesome/fontawesome-svg-core';
import { faBan, faTriangleExclamation } from '@fortawesome/free-solid-svg-icons';
import { faFileLines } from '@fortawesome/free-regular-svg-icons';
import { Button } from '../../../../../components/Button';
import type { ProductView } from '../../../../../services/products/products.service';
import { formatMoneyCompact } from '../../../../../utils/money';
import { SUCCESS_TAGS } from './product-info.constants';
import classes from './product-info-modal.module.scss';

export type ProductInfoModalProps = {
  product: ProductView | null;
  categoryName: string | null;
  roundAmounts?: boolean;
  onClose: () => void;
  onAdd: (productId: number) => void;
  isAdding: boolean;
};

// Summary: Read-only product info card (bottom sheet on phones) with an add-to-order action.
export const ProductInfoModal: React.FC<ProductInfoModalProps> = ({
  product,
  categoryName,
  roundAmounts,
  onClose,
  onAdd,
  isAdding,
}) => {
  const isOutOfStock = product?.limit === 0;
  const isEmpty = product !== null
    && !product.description
    && !product.staffNote
    && product.ingredients.length === 0
    && product.allergens.length === 0
    && product.tags.length === 0;

  return (
    <Modal.Root
      opened={product !== null}
      onClose={onClose}
      centered
      size={480}
      dir="rtl"
      classNames={{ root: classes.root, inner: classes.inner, content: classes.content, body: classes.body }}
    >
      <Modal.Overlay />
      <Modal.Content>
        <Modal.Body>
          {product && (
            <>
          <span className={classes.handle} aria-hidden="true" />
          <div className={classes.header}>
            <div className={classes.titleBlock}>
              {categoryName && <span className={classes.category}>{categoryName}</span>}
              <Modal.Title className={classes.name}>{product.name}</Modal.Title>
            </div>
            <div className={classes.headerActions}>
              <span className={classes.price}>₪{formatMoneyCompact(product.price, roundAmounts)}</span>
            </div>
          </div>
          <div className={classes.scroll}>
            {isOutOfStock && (
              <div className={classes.outOfStock} role="alert">
                <FontAwesomeIcon icon={faBan as IconProp} />
                <span>אזל מהמלאי. לא ניתן להוסיף להזמנה כרגע.</span>
              </div>
            )}
            {isEmpty ? (
              <div className={classes.empty}>
                <FontAwesomeIcon icon={faFileLines as IconProp} className={classes.emptyIcon} />
                <strong>עדיין לא הוזן תיאור למנה הזו</strong>
                <span>מנהל המסעדה יכול להוסיף תיאור, מרכיבים ואלרגנים במסך עריכת המוצר.</span>
              </div>
            ) : (
              <>
                {product.tags.length > 0 && (
                  <div className={classes.chips}>
                    {product.tags.map((tag) => (
                      <span key={tag} className={`${classes.tag} ${SUCCESS_TAGS.has(tag) ? classes.tagSuccess : ''}`}>
                        {tag}
                      </span>
                    ))}
                  </div>
                )}
                {product.description && <p className={classes.description}>{product.description}</p>}
                {product.ingredients.length > 0 && (
                  <section className={classes.section}>
                    <span className={classes.label}>מרכיבים</span>
                    <div className={classes.chips}>
                      {product.ingredients.map((ingredient) => (
                        <span key={ingredient} className={classes.ingredient}>{ingredient}</span>
                      ))}
                    </div>
                  </section>
                )}
                {product.allergens.length > 0 && (
                  <section className={classes.section}>
                    <span className={classes.label}>אלרגנים</span>
                    <div className={classes.chips}>
                      {product.allergens.map((allergen) => (
                        <span key={allergen} className={classes.allergen}>
                          <FontAwesomeIcon icon={faTriangleExclamation as IconProp} />
                          {allergen}
                        </span>
                      ))}
                    </div>
                  </section>
                )}
                {product.staffNote && (
                  <div className={classes.staffNote}>
                    <span className={classes.label}>הערה לצוות</span>
                    <p>{product.staffNote}</p>
                  </div>
                )}
              </>
            )}
          </div>
          <div className={classes.footer}>
            <Button variant="cancel" className={classes.closeButton} dataTestId="product-info-cancel" onClick={onClose}>
              סגור
            </Button>
            <Button
              variant="confirm"
              fullWidth
              dataTestId="product-info-add"
              disabled={isOutOfStock || isAdding}
              onClick={() => onAdd(product.id)}
            >
              הוסף להזמנה
            </Button>
          </div>
        </>
      )}
        </Modal.Body>
      </Modal.Content>
    </Modal.Root>
  );
};
